/**
 * Hook personalizado para manejar el estado de combustibles
 * 
 * Funcionalidades:
 * - Cargar consumos desde la API al montar
 * - Mantener estado local sincronizado
 * - Guardar/actualizar consumos en la API
 * - Buscar consumos existentes
 * - Agrupar consumos por marca
 * 
 * Uso:
 * const {
 *   consumos,
 *   consumosByMarca,
 *   loading,
 *   error,
 *   guardarConsumo,
 *   buscarConsumo,
 *   refrescarConsumos
 * } = useCombustibles(addNotification);
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  obtenerConsumos, 
  guardarConsumo as guardarConsumoAPI, 
  actualizarConsumo as actualizarConsumoAPI,
  transformarConsumosParaDashboard,
  buscarConsumoExistente 
} from '../services/combustibleService';

// export const useCombustibles = (addNotification) => {
export const useCombustibles = (addNotification, options = {}) => {
  const { autoLoad = false } = options;

  const [consumos, setConsumos] = useState([]);
  const [loading, setLoading] = useState(autoLoad);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const autoLoadedRef = useRef(false);

  const norm = (v) => String(v ?? '').trim().toUpperCase();
  const normKey = (c) => `${norm(c.marca)}|${norm(c.familia)}|${norm(c.ano_modelo)}|${norm(c.catalogo_codigo)}`;


  /**
   * Cargar consumos desde la API
   */
const cargarConsumos = useCallback(async () => {
  try {
    setLoading(true);
    setError(null);
    
    const consumosAPI = await obtenerConsumos();
    const consumosTransformados = transformarConsumosParaDashboard(consumosAPI);
    
    setConsumos(consumosTransformados);
    
    console.log(`✅ Cargados ${consumosTransformados.length} consumos desde la API`);
    
    // 👉 importante: regresar los datos
    return consumosTransformados;

  } catch (err) {
    setError(err.message);
    console.error('🔥 Error al cargar consumos:', err);
    
    if (addNotification) {
      addNotification({
        type: 'error',
        title: 'Error al cargar datos',
        message: 'No se pudieron cargar los consumos registrados',
        duration: 5000
      });
    }

    throw err;
  } finally {
    setLoading(false);
  }
}, [addNotification]);

  useEffect(() => {
    if (!autoLoad) return;

    // ✅ evita loop aunque cambie addNotification/cargarConsumos
    if (autoLoadedRef.current) return;
    autoLoadedRef.current = true;

    cargarConsumos();
  }, [autoLoad, cargarConsumos]);


  /**
   * Guardar consumo (crear o actualizar)
   */
  const guardarConsumo = useCallback(async (datosConsumo) => {
  try {
    setIsSubmitting(true);
    
    // Validaciones básicas
    if (!datosConsumo.marca || !datosConsumo.familia || !datosConsumo.consumo_km_l) {
      throw new Error('Faltan datos requeridos para guardar el consumo');
    }

    if (datosConsumo.consumo_km_l <= 0) {
      throw new Error('El consumo debe ser mayor a 0 km/l');
    }

    // Buscar si ya existe un consumo para esta combinación
    const criterio = {
      marca: datosConsumo.marca,
      familia: datosConsumo.familia,
      ano_modelo: datosConsumo.ano_modelo,
      catalogo_codigo: datosConsumo.catalogo_codigo,
    };

    // (opcional) si quieres “nota”: validar que coincide la combinación completa
    const existentePorKey = indexByKey.get(normKey(criterio)) || null;

    // el que manda para UPDATE es el del catalogo_codigo, porque así lo valida el backend
    const consumoExistente = existentePorKey;

    // Preparar payload para la API
    const payload = {
      marca: datosConsumo.marca,
      familia: datosConsumo.familia,
      ano_modelo: datosConsumo.ano_modelo,
      modelo_label: datosConsumo.modelo_label,
      catalogo_codigo: datosConsumo.catalogo_codigo,
      consumo_km_l: datosConsumo.consumo_km_l
    };

    let resultado;

    if (consumoExistente && consumoExistente.id) {
      // 🔁 Ya existe → PUT con ID
      resultado = await actualizarConsumoAPI(consumoExistente.id, payload);
    } else {
      // 🆕 No existe → POST
      resultado = await guardarConsumoAPI(payload);
    }
    
    // Actualizar estado local
    const key = `${payload.marca}|${payload.familia}|${payload.ano_modelo}|${payload.catalogo_codigo}`;
    
    const nuevoConsumo = {
      ...payload,
      key,
      id: resultado.id || consumoExistente?.id || Date.now(), // conservar ID si ya existía
      fecha_creacion: resultado.fecha_creacion || consumoExistente?.fecha_creacion || new Date().toISOString(),
      fecha_actualizacion: resultado.fecha_actualizacion || new Date().toISOString(),
      consumo_km_l: parseFloat(payload.consumo_km_l)
    };

    setConsumos(prev => {
      // Filtrar el existente si lo hay
      const filtrado = prev.filter(c => c.key !== key);
      return [...filtrado, nuevoConsumo];
    });

    // Notificación de éxito
    const tipoOperacion = consumoExistente ? 'actualizado' : 'guardado';
    if (addNotification) {
      addNotification({
        type: 'success',
        title: `Consumo ${tipoOperacion}`,
        message: `${payload.consumo_km_l} km/l para ${payload.marca} ${payload.modelo_label} ${tipoOperacion} correctamente`,
        duration: 4000
      });
    }

    return nuevoConsumo;

  } catch (err) {
    console.error('🔥 Error al guardar consumo:', err);
    
    if (addNotification) {
      addNotification({
        type: 'error',
        title: 'Error al guardar',
        message: err.message || 'Ocurrió un error al guardar el consumo',
        duration: 5000
      });
    }
    
    throw err;
  } finally {
    setIsSubmitting(false);
  }
}, [consumos, addNotification]);


  /**
   * Buscar consumo existente por combinación
   */
  const buscarConsumo = useCallback((criterio) => {
    return buscarConsumoExistente(consumos, criterio);
  }, [consumos]);

  /**
   * Refrescar consumos desde la API
   */
const refrescarConsumos = useCallback(() => {
  return cargarConsumos();
}, [cargarConsumos]);


  /**
   * Mapa de consumos agrupados por marca
   */
  const consumosByMarca = useMemo(() => {
    const mapa = new Map();
    consumos.forEach(consumo => {
      if (!mapa.has(consumo.marca)) {
        mapa.set(consumo.marca, []);
      }
      mapa.get(consumo.marca).push(consumo);
    });
    return mapa;
  }, [consumos]);

  /**
   * Obtener consumos de una marca específica
   */
  const obtenerConsumosDeMarca = useCallback((marca) => {
    return consumosByMarca.get(marca) || [];
  }, [consumosByMarca]);

  /**
   * Verificar si una marca tiene consumos registrados
   */
  const marcaTieneConsumos = useCallback((marca) => {
    return consumosByMarca.has(marca) && consumosByMarca.get(marca).length > 0;
  }, [consumosByMarca]);

  const indexByCatalogo = useMemo(() => {
  const m = new Map();
  consumos.forEach(c => {
    const cc = norm(c.catalogo_codigo);
    if (cc) m.set(cc, c); // si hay duplicados, el último gana
  });
  return m;
}, [consumos]);

const indexByKey = useMemo(() => {
  const m = new Map();
  consumos.forEach(c => m.set(normKey(c), c));
  return m;
}, [consumos]);


  return {
    // Estado
    consumos,
    consumosByMarca,
    loading,
    error,
    isSubmitting,
    
    // Acciones
    guardarConsumo,
    buscarConsumo,
    refrescarConsumos,
    obtenerConsumosDeMarca,
    marcaTieneConsumos,
    
    // Utilidades
    totalConsumos: consumos.length,
    marcasConConsumos: Array.from(consumosByMarca.keys()),
  };
};

export default useCombustibles;